import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { Party, WizardState } from '../api';
import { SectionCard } from '../ui/components';
import { ActionQueueCard } from '../ui/mobileOps';
import {
  Button,
  ErrorBanner,
  Pill,
  Spinner,
  TextInput,
} from '../ui/primitives';

export function StepSelf({ wizard }: { wizard: WizardState }) {
  const qc = useQueryClient();
  const { data: parties, isLoading } = useQuery<Party[]>({
    queryKey: ['parties', 'self'],
    queryFn: () => api.get<Party[]>('/api/parties?role=self'),
  });
  const [displayName, setDisplayName] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: (name: string) => api.post<Party>('/api/parties', { role: 'self', displayName: name }),
    onSuccess: () => {
      setDisplayName('');
      setLocalError(null);
      void qc.invalidateQueries();
    },
    onError: (err) => {
      if (err instanceof ApiError) setLocalError(err.message);
      else setLocalError(String(err));
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => api.patch<Party>(`/api/parties/${id}`, { displayName: name }),
    onSuccess: () => void qc.invalidateQueries(),
  });

  const self = parties?.[0] ?? null;

  return (
    <div className="space-y-4">
      <ActionQueueCard
        section="accounts"
        eyebrow="Step 1"
        title="Create your self party first"
        description="This is the party that represents you in the books. Margin calculations ultimately settle here, and there can only be one self party."
        tone="accent"
        meta={
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <span>Already created</span>
              <span className="font-medium text-ink">{wizard.counts.selfParties}</span>
            </div>
            <div className="text-[12px] leading-5 text-ink-muted">
              The name is internal. Clients never see it on their side.
            </div>
          </div>
        }
      />

      <SectionCard section="accounts" title="Your self party">
        {isLoading ? (
          <div className="flex items-center gap-2 text-small text-ink-muted">
            <Spinner /> loading...
          </div>
        ) : self ? (
          <div className="space-y-4">
            <div className="rounded-[22px] border border-border/70 bg-surface p-4 shadow-sm backdrop-blur">
              <div className="flex flex-wrap items-center gap-2">
                <Pill tone="green">Exists</Pill>
                <span className="text-small font-semibold tracking-[-0.01em] text-ink">{self.displayName}</span>
              </div>
              <div className="mt-2 text-[12px] leading-5 text-ink-muted">
                You can rename it here whenever needed. That does not change the underlying role.
              </div>
            </div>
            <SelfRenameForm
              key={self.id}
              initial={self.displayName}
              onSave={(name) => updateMut.mutate({ id: self.id, name })}
              saving={updateMut.isPending}
            />
          </div>
        ) : (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              const name = displayName.trim();
              if (!name) {
                setLocalError('Please enter a display name.');
                return;
              }
              createMut.mutate(name);
            }}
          >
            <TextInput
              label="Display name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Me, or your first name"
              hint="Only visible inside this app."
              name="displayName"
            />
            {localError && <ErrorBanner>{localError}</ErrorBanner>}
            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" disabled={createMut.isPending}>
                {createMut.isPending ? <Spinner /> : 'Create self party'}
              </Button>
              {wizard.counts.uplineParties > 0 && (
                <span className="text-caption text-ink-muted">If this already exists elsewhere, refresh once.</span>
              )}
            </div>
          </form>
        )}
      </SectionCard>
    </div>
  );
}

function SelfRenameForm({
  initial,
  onSave,
  saving,
}: {
  initial: string;
  onSave: (name: string) => void;
  saving: boolean;
}) {
  const [value, setValue] = useState(initial);
  const changed = value.trim() !== initial;
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (changed) onSave(value.trim());
      }}
    >
      <TextInput label="Rename self party" value={value} onChange={(e) => setValue(e.target.value)} name="rename" />
      <Button type="submit" variant="secondary" disabled={!changed || saving || value.trim().length === 0}>
        {saving ? <Spinner /> : 'Save name'}
      </Button>
    </form>
  );
}
